import React, { useEffect, useRef, useState } from "react";
import { DrawerForm, DrawerFormRef } from "../../../../components/drawers/DrawerForm";
import PlanCatalog, { PlanBilling } from "../../../../components/plans/PlanCatalog";
import PlanUpgradeConfirmDrawer from "./PlanUpgradeConfirmDrawer";
import { NEW_CARD } from "./PaymentMethodPicker";
import { toastError, toastSuccess } from "../../../../components/base/Toast";
import { useHub } from "../../../../core/Global";
import CurrentPlan from "../models/CurrentPlan";
import DowngradeDrawer from "./DowngradeDrawer";
import "./PlanUpgradeDrawer.css";


// Choosing a plan to move onto.
//
// Picking one is the whole of this drawer, so there is no button under it to press afterwards:
// the card is the choice. It prices the plan and opens the confirm drawer over this one, which is
// where the card is chosen and the charge is made. This one stays open underneath, so backing out
// of the confirm lands back on the plans rather than on the page.
export default function PlanUpgradeDrawer(props: {
    plan: CurrentPlan,
    // Held by the page so it can ask this to close after a purchase. Unmounting it instead would
    // leave the backdrop behind: only the drawer's own hide takes that down.
    drawerRef?: React.RefObject<DrawerFormRef>,
    onClose: () => void,
    onUpgraded: () => void,
    // Opened from an upgrade offer: the plan it suggested is chosen straight away.
    initialPlan?: string,
    // Asked from inside the downgrade, before its confirm button: pause instead.
    onPauseInstead?: () => void
}) {

    const hub = useHub()

    const [billing, billingSet] = useState<PlanBilling>(props.plan.isAnnual ? "annual" : "monthly")
    const [chosen, chosenSet] = useState<string>()
    const [quoting, quotingSet] = useState<boolean>(false)

    const [quote, quoteSet] = useState<any>()
    const confirmRef = useRef<DrawerFormRef>(null)

    const [methodId, methodIdSet] = useState<string>()
    const [loadingMethods, loadingMethodsSet] = useState<boolean>(true)

    // A smaller plan, chosen from the same catalog. It opens over this drawer like the upgrade's
    // confirm does, so backing out lands back on the plans.
    const [downgradeTo, downgradeToSet] = useState<string>()
    const downgradeRef = useRef<DrawerFormRef>(null)

    useEffect(() => {
        if (props.initialPlan && props.initialPlan !== props.plan.planCode) choose(props.initialPlan)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // What is owed depends on how much of the current period is left, so the figure comes from
    // the server rather than from the card that was clicked.
    //
    // quoting is still kept: it holds the second click, so a plan chosen while the first is in
    // flight cannot open the confirm drawer on the wrong one.
    const choose = (code: string) => {
        if (quoting) return

        chosenSet(code)
        quotingSet(true)

        hub.Post("/accounts/profile/plan/upgrade/quote", { planCode: code, billing }, res => {
            quotingSet(false)
            if (!res.success) {
                toastError(res.message ?? "Couldn't price that plan.")
                chosenSet(undefined)
                return
            }
            quoteSet(res.data)
        }, () => {
            quotingSet(false)
            chosenSet(undefined)
            toastError("Couldn't price that plan.")
        })
    }

    // The other way to upgrade: booked for the renewal date, nothing charged today.
    const schedule = (callback: any) => {
        hub.Post("/accounts/profile/plan/upgrade/schedule", {
            planCode: quote.planCode,
            billing: quote.billing
        }, res => {
            callback(res)
            if (res.success) { toastSuccess("Your upgrade is scheduled for your renewal date."); props.onUpgraded() }
            else toastError(res.message ?? "Couldn't schedule the upgrade.")
        }, () => {
            toastError("Couldn't schedule the upgrade.")
            callback({ success: false })
        })
    }

    const pay = (callback: any) => {

        // A card we have never seen is entered on Stripe's own page. The browser leaves, and the
        // upgrade is finished by the endpoint Stripe returns it to -- so there is no success to
        // report back here, and the callback is never reached.
        if (methodId === NEW_CARD) {
            hub.Post("/billings/payments/plan/newcard", {
                planCode: quote.planCode,
                billing: quote.billing
            }, res => {
                if (res.success && res.data?.url) { document.location.href = res.data.url; return }
                toastError(res.message ?? "Couldn't start the payment.")
                callback({ success: false })
            }, () => {
                toastError("Couldn't start the payment.")
                callback({ success: false })
            })
            return
        }

        hub.Post("/billings/payments/plan", {
            planCode: quote.planCode,
            billing: quote.billing,
            paymentMethodId: methodId
        }, res => {
            callback(res)
            // The confirm drawer closes itself on success; this one is closed by the page, once
            // it has been told.
            if (res.success) props.onUpgraded()
            else toastError(res.message ?? "Couldn't complete the upgrade.")
        }, () => {
            toastError("Couldn't complete the upgrade.")
            callback({ success: false })
        })
    }

    return (
        <>
            <DrawerForm id="drawer-plan-upgrade"
                ref={props.drawerRef}
                title="Change Plan"
                titleIcon="arrow-up-right-dots"
                subtitle={"You are on " + (props.plan.displayName ?? props.plan.planName)}
                size="xlarge"
                hideButtons
                onClose={props.onClose}>

                {
                    /* On a paid plan, Free is on the cards as the plan to move down to: that is how
                       a paid plan is left, scheduled for the renewal date like any downgrade. On
                       Free itself it is left out -- it is where they already stand. */
                }
                <PlanCatalog
                    value={chosen}
                    billing={billing}
                    onBillingChange={billingSet}
                    onSelect={choose}
                    showEnterprise={false}
                    paidOnly={!!props.plan.isFree}
                    currentCode={props.plan.planCode}
                    currentBilling={props.plan.isAnnual ? "annual" : "monthly"}
                    currentPrice={props.plan.price}
                    onDowngrade={code => downgradeToSet(code)} />

            </DrawerForm>

            {
                downgradeTo &&
                <DowngradeDrawer
                    plan={props.plan}
                    planCode={downgradeTo}
                    drawerRef={downgradeRef}
                    onClose={() => downgradeToSet(undefined)}
                    // Scheduled: the page closes this drawer and re-reads the plan, as it does
                    // after an upgrade.
                    onScheduled={() => props.onUpgraded()}
                    onPauseInstead={() => props.onPauseInstead?.()} />
            }

            {
                quote &&
                <PlanUpgradeConfirmDrawer
                    quote={quote}
                    plan={props.plan}
                    drawerRef={confirmRef}
                    onSchedule={schedule}
                    methodId={methodId}
                    onMethodChange={methodIdSet}
                    loadingMethods={loadingMethods}
                    onLoadingMethodsChange={loadingMethodsSet}
                    onPay={pay}
                    onClose={() => quoteSet(undefined)} />
            }
        </>
    )
}
